import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Phone, Scale, FileText, Users, TrendingUp } from 'lucide-react';
import { serviceDetails } from '../data/mock';
import ContactFormModal from '../components/ContactFormModal';

const ServicePage = () => {
  const { slug } = useParams();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  
  const service = serviceDetails[slug];

  React.useEffect(() => {
    if (service) {
      document.title = `${service.title} - IP Partners Inc`;
    }
  }, [service]);

  if (!service) {
    return (
      <div className="min-h-screen bg-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-brown-900 mb-4">Service Not Found</h1>
          <p className="text-lg text-brown-700 mb-8">
            The service you're looking for doesn't exist or has been moved.
          </p>
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-brown-700 hover:text-brown-800 font-semibold"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brown-100 to-brown-50 pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-brown-700 hover:text-brown-800 font-semibold mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-brown-900 mb-4">
            {service.title}
          </h1>
          <p className="text-xl text-brown-700 max-w-3xl">
            {service.description}
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Overview */}
              <div>
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Overview</h2>
                <p className="text-lg text-brown-700 leading-relaxed mb-6">
                  {service.fullDescription}
                </p>
              </div>

              {/* Why Choose This Service */}
              <div className="bg-brown-50 rounded-xl p-8">
                <h2 className="text-2xl font-bold text-brown-900 mb-6">Why Choose This Service</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {service.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-6 h-6 text-brown-600 flex-shrink-0 mt-0.5" />
                      <span className="text-brown-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Our Process */}
              <div>
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Our Process</h2>
                <p className="text-brown-700 mb-6">
                  We follow a structured, client-focused approach to ensure the best outcomes:
                </p>
                <div className="space-y-4">
                  {service.process.map((step, index) => (
                    <div key={index} className="flex items-start space-x-4 p-6 bg-brown-50 rounded-lg border border-brown-200">
                      <div className="bg-brown-700 text-white w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-semibold">
                        {index + 1}
                      </div>
                      <p className="text-brown-700 pt-2">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* What to Expect */}
              <div className="bg-brown-900 rounded-xl p-8 text-white">
                <h2 className="text-2xl font-bold mb-6">What to Expect</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-brown-300" />
                    <h3 className="font-semibold mb-2">Clear Communication</h3>
                    <p className="text-brown-200 text-sm">Regular updates throughout the process</p>
                  </div>
                  <div className="text-center">
                    <Users className="w-12 h-12 mx-auto mb-3 text-brown-300" />
                    <h3 className="font-semibold mb-2">Expert Team</h3>
                    <p className="text-brown-200 text-sm">Experienced attorneys handling your case</p>
                  </div>
                  <div className="text-center">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 text-brown-300" />
                    <h3 className="font-semibold mb-2">Proven Results</h3>
                    <p className="text-brown-200 text-sm">Track record of successful outcomes</p>
                  </div>
                </div>
              </div>

              {/* Why This Matters */}
              <div className="bg-brown-900 rounded-xl p-8 text-white">
                <h2 className="text-2xl font-bold mb-6">Why This Matters for Your Business</h2>
                <p className="text-brown-100 mb-6 leading-relaxed">
                  In today's complex legal landscape, having experienced counsel makes the difference between success 
                  and costly mistakes. Our attorneys understand the nuances of {service.title.toLowerCase()} and provide 
                  strategic guidance that protects your interests while enabling your business objectives.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-brown-800 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Risk Mitigation</h3>
                    <p className="text-brown-200 text-sm">Identify and address potential legal risks before they become problems</p>
                  </div>
                  <div className="bg-brown-800 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Strategic Advantage</h3>
                    <p className="text-brown-200 text-sm">Leverage legal strategies that give you competitive edge in your industry</p>
                  </div>
                  <div className="bg-brown-800 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Cost Efficiency</h3>
                    <p className="text-brown-200 text-sm">Prevent expensive litigation and compliance issues through proactive counsel</p>
                  </div>
                  <div className="bg-brown-800 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Peace of Mind</h3>
                    <p className="text-brown-200 text-sm">Focus on growing your business while we handle the legal complexities</p>
                  </div>
                </div>
              </div>

              {/* Industry Applications */}
              <div>
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Industry Applications</h2>
                <p className="text-brown-700 mb-6">
                  Our {service.title.toLowerCase()} services benefit organizations across diverse industries:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3 p-4 bg-brown-50 rounded-lg border border-brown-200">
                    <CheckCircle className="w-5 h-5 text-brown-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-brown-900 mb-1">Technology & Software</h3>
                      <p className="text-sm text-brown-700">Startups, SaaS companies, and tech enterprises</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-brown-50 rounded-lg border border-brown-200">
                    <CheckCircle className="w-5 h-5 text-brown-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-brown-900 mb-1">Healthcare & Life Sciences</h3>
                      <p className="text-sm text-brown-700">Medical practices, biotech, and pharmaceutical companies</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-brown-50 rounded-lg border border-brown-200">
                    <CheckCircle className="w-5 h-5 text-brown-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-brown-900 mb-1">Manufacturing & Distribution</h3>
                      <p className="text-sm text-brown-700">Industrial companies and supply chain businesses</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-brown-50 rounded-lg border border-brown-200">
                    <CheckCircle className="w-5 h-5 text-brown-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-brown-900 mb-1">Financial Services</h3>
                      <p className="text-sm text-brown-700">Banks, investment firms, and fintech companies</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-brown-50 rounded-lg border border-brown-200">
                    <CheckCircle className="w-5 h-5 text-brown-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-brown-900 mb-1">Real Estate & Construction</h3>
                      <p className="text-sm text-brown-700">Developers, contractors, and property management</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-brown-50 rounded-lg border border-brown-200">
                    <CheckCircle className="w-5 h-5 text-brown-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-brown-900 mb-1">Retail & E-Commerce</h3>
                      <p className="text-sm text-brown-700">Online sellers, brick-and-mortar stores, and marketplaces</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Common Questions */}
              <div>
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Common Questions</h2>
                <div className="space-y-4">
                  <div className="border-l-4 border-brown-600 pl-6 py-2">
                    <h3 className="font-semibold text-brown-900 mb-2">How long does the process take?</h3>
                    <p className="text-brown-700">
                      Timelines vary based on complexity, but we provide estimated timeframes during your initial consultation
                      and keep you updated throughout the process.
                    </p>
                  </div>
                  <div className="border-l-4 border-brown-600 pl-6 py-2">
                    <h3 className="font-semibold text-brown-900 mb-2">What documents do I need?</h3>
                    <p className="text-brown-700">
                      Required documents vary by service. We'll provide a comprehensive checklist after your initial consultation
                      to ensure you have everything needed.
                    </p>
                  </div>
                  <div className="border-l-4 border-brown-600 pl-6 py-2">
                    <h3 className="font-semibold text-brown-900 mb-2">How much does it cost?</h3>
                    <p className="text-brown-700">
                      Our fees are transparent and competitive. We offer detailed quotes after understanding your specific needs
                      and circumstances during the consultation.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - CTA Card (Sticky) */}
            <div className="lg:col-span-1">
              <div className="bg-brown-50 rounded-xl p-8 shadow-lg sticky top-24 border border-brown-200">
                <div className="mb-6">
                  <Scale className="w-16 h-16 text-brown-700 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-brown-900 text-center mb-2">
                    Need Legal Assistance?
                  </h3>
                  <p className="text-brown-700 text-center">
                    Connect with an experienced attorney today
                  </p>
                </div>
                
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="w-full bg-brown-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-brown-800 transition-colors duration-200 mb-4"
                >
                  Get Legal Help
                </button>
                
                <div className="border-t border-brown-300 pt-6 mt-6">
                  <div className="flex items-center space-x-3 text-brown-700 mb-4">
                    <Phone className="w-5 h-5 text-brown-600" />
                    <div>
                      <p className="text-sm text-brown-600">Call us directly</p>
                      <a href="tel:+18332017574" className="font-semibold hover:text-brown-800">
                        +1 (833) 201-7574
                      </a>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-brown-300">
                  <h4 className="font-semibold text-brown-900 mb-3">What You Get</h4>
                  <ul className="space-y-3 text-sm text-brown-700">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-brown-600" />
                      <span>Free initial consultation</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-brown-600" />
                      <span>Licensed attorneys</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-brown-600" />
                      <span>Transparent pricing</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-brown-600" />
                      <span>Confidential service</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-brown-600" />
                      <span>Fast response times</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-brown-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-brown-900 mb-8 text-center">Related Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/services/corporate-business-contracts" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-lg font-semibold text-brown-900 mb-2">Corporate & Business Contracts</h3>
              <p className="text-brown-700 text-sm">Professional contract drafting and review services</p>
            </Link>
            <Link to="/services/business-transaction" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-lg font-semibold text-brown-900 mb-2">Business Transaction</h3>
              <p className="text-brown-700 text-sm">Expert guidance for complex business transactions</p>
            </Link>
            <Link to="/services/mergers-acquisitions" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-lg font-semibold text-brown-900 mb-2">Mergers & Acquisitions</h3>
              <p className="text-brown-700 text-sm">Strategic legal counsel for M&A transactions</p>
            </Link>
          </div>
        </div>
      </section>

      <ContactFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default ServicePage;
